import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';

import {
  Pressable,
  StyleSheet,
  Text,
  View,
  FlatList,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { isTablet } from 'react-native-device-info';
import FastImage from '@d11/react-native-fast-image';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { GTFonts, useLayoutSize, useTheme } from '../../theme';
import {
  fs,
  s,
  v,
  formatWithCommas,
  getCurrencyDisplayFormat,
  forceHttps,
  iconSize,
  borderWidth,
} from '../../utils/libs';
import { shouldShowTabletSidebarLayoutSetting } from '../../utils/layoutUtils';
import { getProductDisplayName } from '../../utils/productDisplayName';
import { ActivityIndicator, Icon } from 'react-native-paper';
import AppSearchBar from '../../components/Reusables/AppSearchBar';
import AppSearchBarWeb from '../../components/Reusables/AppSearchBar.web';
import Button from '../../components/Reusables/Button';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../../navigation/AppStack';
import WebHeaderBar from '../../components/web/WebHeaderBar';
import useWebHeader from '../../components/web/useWebHeader';
import {
  setProducts,
  setProductsError,
  setProductsLoading,
  setProductsPagination,
  setSearchTerm,
  setSelectedProductId,
  ensureProductsListInitialized,
  setTableSearchTerm,
  setTableSearchResults,
  setTableSearchPagination,
  clearTableSearch,
} from '../../redux/backoffice/productsSlice';
import {
  listOwnerProducts,
  searchOwnerProducts,
} from '../../api/backofficeProducts';

const getProductVariants = (product: any) =>
  Array.isArray(product?.bp_product_variants) ? product.bp_product_variants : [];

const isVariantBackofficeProduct = (product: any): boolean =>
  Boolean(product?.bp_product_has_variants && getProductVariants(product).length);

const getVariantAggregateStock = (product: any): number =>
  getProductVariants(product).reduce((sum: number, variant: any) => {
    const qty = Number(variant?.bp_variant_quantity ?? 0);
    return sum + (Number.isFinite(qty) ? qty : 0);
  }, 0);

const getVariantPriceRangeLabel = (
  product: any,
  currencyText: string,
): string => {
  const variants = getProductVariants(product);
  if (!variants.length) return '—';
  const prices = variants
    .map((variant: any) => Number(variant?.bp_variant_selling_price ?? 0))
    .filter((price: number) => Number.isFinite(price));
  if (!prices.length) return '—';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return `${currencyText} ${formatWithCommas(min, 0)}`;
  return `${currencyText} ${formatWithCommas(min, 0)} - ${currencyText} ${formatWithCommas(max, 0)}`;
};

const isBackofficeProductLowStock = (product: any): boolean => {
  if (isVariantBackofficeProduct(product)) {
    const variants = getProductVariants(product);
    if (!variants.length) return true;
    return variants.some((variant: any) => {
      const qty = Number(variant?.bp_variant_quantity ?? 0);
      const minRaw = variant?.bp_variant_minimum_stock_level;
      const hasMin = !(minRaw == null || String(minRaw).trim() === '');
      const min = hasMin ? Number(minRaw) : null;
      if (typeof min === 'number' && !Number.isNaN(min)) {
        return qty <= 0 || qty < min;
      }
      return qty <= 0;
    });
  }

  const qty = Number(product?.bp_product_quantity ?? 0);
  const minRaw = product?.bp_minimum_stock_level;
  const hasMin = !(minRaw == null || String(minRaw).trim() === '');
  const min = hasMin ? Number(minRaw) : null;
  if (typeof min === 'number' && !Number.isNaN(min)) {
    return qty <= 0 || qty < min;
  }
  return qty <= 0;
};

const getLowVariantLabels = (product: any): string[] => {
  if (!isVariantBackofficeProduct(product)) return [];
  return getProductVariants(product)
    .filter((variant: any) => {
      const qty = Number(variant?.bp_variant_quantity ?? 0);
      const minRaw = variant?.bp_variant_minimum_stock_level;
      const hasMin = !(minRaw == null || String(minRaw).trim() === '');
      const min = hasMin ? Number(minRaw) : null;
      if (typeof min === 'number' && !Number.isNaN(min)) {
        return qty <= 0 || qty < min;
      }
      return qty <= 0;
    })
    .map((variant: any, index: number) => {
      const explicit = String(variant?.bp_variant_display_name || '').trim();
      if (explicit) return explicit;
      const attrs =
        variant?.bp_variant_attributes &&
        typeof variant.bp_variant_attributes === 'object'
          ? Object.values(variant.bp_variant_attributes)
              .map(value => String(value || '').trim())
              .filter(Boolean)
              .join(' / ')
          : '';
      if (attrs) return attrs;
      return `Variant ${index + 1}`;
    });
};

const BackOfficeProductsScreen: React.FC = () => {
  const { width, height } = useWindowDimensions();
  const nativeHeaderSearchWidth = isTablet()
    ? Math.max(width * 0.7, width - s(110))
    : width * 0.7;
  const { useSidebarNav } = useLayoutSize();
  const showTabletSidebarLayoutSetting = shouldShowTabletSidebarLayoutSetting(
    width,
    height,
  );
  const [showAdd, setShowAdd] = useState(false);
  const [filterMode, setFilterMode] = useState<'all' | 'low'>('all');
  useEffect(() => {
    // Honor route params to preselect low stock filter
    const wantsLow = (nav as any)
      ?.getState?.()
      ?.routes?.some?.((r: any) =>
        String(r?.params?.preset).toLowerCase?.().includes?.('low'),
      );
    if (wantsLow) setFilterMode('low');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const nav = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const headerRightNode = useMemo(
    () => (
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          marginRight: s(8),
        }}>
        <Pressable
          onPress={() => nav.navigate('BackOfficeAddProduct')}
          style={({ pressed, hovered }: any) => [
            styles.addProductBtn,
            pressed && styles.addProductBtnPressed,
            Platform.OS === 'web' && hovered && styles.addProductBtnHovered,
          ]}>
          <Text style={styles.addProductBtnText}>+ Add New</Text>
        </Pressable>
      </View>
    ),
    [
      nav,
      styles.addProductBtn,
      styles.addProductBtnHovered,
      styles.addProductBtnPressed,
      styles.addProductBtnText,
    ],
  );

  const dispatch = useAppDispatch();
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const employee = useAppSelector(
    s => s.auth.inApp.account.employeeInfo as any,
  );
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currencyText = useMemo(
    () => getCurrencyDisplayFormat(currency),
    [currency],
  );

  const ownerId = String(employee?.bp_owner_id || '');
  const locationVersion = useAppSelector(s => s.location.locationVersion);

  const items = useAppSelector(s => s.backofficeProducts?.list?.items ?? []);
  const loading = useAppSelector(
    s => s.backofficeProducts?.list?.loading ?? false,
  );
  const page = useAppSelector(s => s.backofficeProducts?.list?.page ?? 1);
  const pageSize = useAppSelector(
    s => s.backofficeProducts?.list?.pageSize ?? 500,
  );
  const total = useAppSelector(s => s.backofficeProducts?.list?.total ?? 0);
  const searchText = useAppSelector(
    s => s.backofficeProducts?.list?.searchTerm ?? '',
  );
  // Table search state
  const tableSearchTerm = useAppSelector(
    s => s.backofficeProducts?.list?.tableSearchTerm ?? '',
  );
  const tableSearchItems = useAppSelector(
    s => s.backofficeProducts?.list?.tableSearchItems ?? [],
  );
  const tableSearchTotal = useAppSelector(
    s => s.backofficeProducts?.list?.tableSearchTotal ?? 0,
  );
  const tableSearchPage = useAppSelector(
    s => s.backofficeProducts?.list?.tableSearchPage ?? 1,
  );
  const tableSearchPageSize = useAppSelector(
    s => s.backofficeProducts?.list?.tableSearchPageSize ?? 100,
  );

  const searchBar = useMemo(
    () => (
      <View
        style={{
          width: '100%',
          maxWidth: nativeHeaderSearchWidth,
        }}>
        <AppSearchBar
          placeholder="Search products by name"
          value={tableSearchTerm}
          onChangeText={text => dispatch(setTableSearchTerm(text))}
          onClear={() => dispatch(clearTableSearch())}
          showLeadingIcon={true}
          showBarcodeScan={false}
          width={nativeHeaderSearchWidth}
          height={v(50)}
          containerStyle={{
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.surface,
            borderWidth: 0,
          }}
        />
      </View>
    ),
    [dispatch, nativeHeaderSearchWidth, tableSearchTerm],
  );

  const webSearchBar = useMemo(
    () => (
      <View
        style={{
          width: '100%',
          maxWidth: width >= 768 && width < 1024 ? s(660) : s(620),
        }}>
        <AppSearchBarWeb
          placeholder="Search products by name"
          value={tableSearchTerm}
          onChangeText={text => dispatch(setTableSearchTerm(text))}
          onClear={() => dispatch(clearTableSearch())}
          showLeadingIcon={true}
          showBarcodeScan={false}
          width={
            showTabletSidebarLayoutSetting && useSidebarNav
              ? width * 0.46
              : width * 0.32
          }
          height={v(50)}
          containerStyle={{
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.surface,
            borderWidth: 0,
          }}
        />
      </View>
    ),
    [dispatch, showTabletSidebarLayoutSetting, tableSearchTerm, useSidebarNav, width],
  );

  useLayoutEffect(() => {
    if (useSidebarNav) return;
    (nav as any).setOptions({
      headerTitleContainerStyle: { flex: 1 },
      headerTitle: () => searchBar,
      headerRight: () => headerRightNode,
    });
  }, [headerRightNode, nav, searchBar, useSidebarNav]);

  const webHeaderNode = useMemo(() => {
    if (!useSidebarNav) return null;
    return (
      <WebHeaderBar
        rightSlot={headerRightNode}
        onBack={() => {
          try {
            (nav as any).navigate('BackOffice', { screen: 'Dashboard' });
          } catch {
            if (nav.canGoBack()) nav.goBack();
          }
        }}>
        {webSearchBar}
      </WebHeaderBar>
    );
  }, [headerRightNode, nav, useSidebarNav, webSearchBar]);

  useWebHeader(webHeaderNode, Boolean(webHeaderNode));

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const initRef = useRef(false);
  const loadMoreLockRef = useRef(false);

  const fetchProducts = useCallback(async () => {
    if (!owner?.accessToken || !ownerId) return;
    try {
      dispatch(setProductsLoading(true));
      dispatch(setProductsError(null));
      const res = await listOwnerProducts(
        axiosBackoffice as any,
        ownerId,
        page,
        pageSize,
        { accessToken: owner?.accessToken },
      );
      console.log('fetchProducts', res?.data?.data);
      if (res?.status === 200) {
        console.log('fetchProducts', res?.data?.data);
        const docs = res?.data?.data?.data || [];
        const totalCount = res?.data?.data?.total || docs.length;
        dispatch(
          setProducts({ items: docs, total: totalCount, page, pageSize }),
        );
      } else {
        dispatch(
          setProductsError(
            String(res?.data?.message || 'Failed to load products'),
          ),
        );
      }
    } catch (e: any) {
      dispatch(setProductsError(e?.message || 'Error fetching products'));
    } finally {
      dispatch(setProductsLoading(false));
    }
  }, [owner?.accessToken, ownerId, dispatch, axiosBackoffice, page, pageSize]);

  const fetchSearch = useCallback(async () => {
    if (!owner?.accessToken || !ownerId || !searchText) return;
    try {
      dispatch(setProductsLoading(true));
      dispatch(setProductsError(null));
      const res = await searchOwnerProducts(
        axiosBackoffice as any,
        searchText,
        page,
        pageSize,
        { accessToken: owner?.accessToken },
      );
      if (res?.status === 200) {
        const docs = res?.data?.data?.data || [];
        const totalCount = res?.data?.data?.total || docs.length;
        dispatch(
          setProducts({ items: docs, total: totalCount, page, pageSize }),
        );
      } else {
        dispatch(
          setProductsError(
            String(res?.data?.message || 'Failed to search products'),
          ),
        );
      }
    } catch (e: any) {
      dispatch(setProductsError(e?.message || 'Error searching products'));
    } finally {
      dispatch(setProductsLoading(false));
    }
  }, [
    owner?.accessToken,
    ownerId,
    dispatch,
    axiosBackoffice,
    page,
    pageSize,
    searchText,
  ]);

  const fetchTableSearch = useCallback(async () => {
    if (!owner?.accessToken || !ownerId || !tableSearchTerm?.trim()) return;
    try {
      dispatch(setProductsLoading(true));
      dispatch(setProductsError(null));
      const res = await searchOwnerProducts(
        axiosBackoffice as any,
        tableSearchTerm,
        tableSearchPage,
        tableSearchPageSize,
        { accessToken: owner?.accessToken },
      );
      if (res?.status === 200) {
        const docs = res?.data?.data?.data || [];
        const totalCount = res?.data?.data?.total || docs.length;
        dispatch(
          setTableSearchResults({
            items: docs,
            total: totalCount,
            page: tableSearchPage,
            pageSize: tableSearchPageSize,
          }),
        );
      } else {
        dispatch(
          setProductsError(
            String(res?.data?.message || 'Failed to search products'),
          ),
        );
      }
    } catch (e: any) {
      dispatch(setProductsError(e?.message || 'Error searching products'));
    } finally {
      dispatch(setProductsLoading(false));
    }
  }, [
    owner?.accessToken,
    ownerId,
    dispatch,
    axiosBackoffice,
    tableSearchTerm,
    tableSearchPage,
    tableSearchPageSize,
  ]);

  // Ensure state shape exists once
  useEffect(() => {
    dispatch(ensureProductsListInitialized());
  }, [dispatch]);

  // Reset init ref when location context changes to allow refetch
  const prevLocationVersion = useRef(locationVersion);
  useEffect(() => {
    if (prevLocationVersion.current !== locationVersion) {
      initRef.current = false;
      prevLocationVersion.current = locationVersion;
    }
  }, [locationVersion]);

  // One-time init: reset pagination to page 1 and fetch first page
  useEffect(() => {
    if (initRef.current) return;
    if (!owner?.accessToken || !ownerId) return;
    initRef.current = true;
    (async () => {
      try {
        // Reset both paginations to avoid persisted high page
        dispatch(setProductsPagination({ page: 1 }));
        dispatch(setTableSearchPagination({ page: 1 }));
        dispatch(setProductsLoading(true));
        dispatch(setProductsError(null));
        const res = await listOwnerProducts(
          axiosBackoffice as any,
          ownerId,
          1,
          pageSize,
          { accessToken: owner?.accessToken },
        );
        if (res?.status === 200) {
          const docs = res?.data?.data?.data || [];
          const totalCount = res?.data?.data?.total || docs.length;
          dispatch(
            setProducts({ items: docs, total: totalCount, page: 1, pageSize }),
          );
        } else {
          dispatch(
            setProductsError(
              String(res?.data?.message || 'Failed to load products'),
            ),
          );
        }
      } catch (e: any) {
        dispatch(setProductsError(e?.message || 'Error fetching products'));
      } finally {
        dispatch(setProductsLoading(false));
      }
    })();
  }, [
    owner?.accessToken,
    ownerId,
    axiosBackoffice,
    dispatch,
    pageSize,
    locationVersion,
  ]);

  // React to global searchText only when it has content (avoid duplicate initial fetch)
  useEffect(() => {
    if (!initRef.current) return;
    if (searchText && searchText.trim().length > 0) {
      fetchSearch();
    }
  }, [searchText, fetchSearch]);

  // Kick off table search fetches (has higher display priority than normal)
  useEffect(() => {
    if (tableSearchTerm && tableSearchTerm.trim().length > 0) {
      fetchTableSearch();
    }
  }, [fetchTableSearch, tableSearchTerm, tableSearchPage, tableSearchPageSize]);

  /*
  // Table columns retained for future use
  const columns: AppDataTableColumn[] = [
    { title: '#', width: s(40), wrap: false },
    { title: 'ProductName', width: s(170) },
    { title: 'Category', width: s(120) },
    { title: 'Sub Category', width: s(120) },
    {
      title: 'Buying Price',
      width: s(120),
      align: 'right',
    },
    {
      title: 'Selling Price',
      width: s(120),
      align: 'right',
    },
    { title: 'Quantity', width: s(100), align: 'right' },
    {
      title: 'Min Stock Level',
      width: s(90),
      align: 'right',
    },
  ];
  */

  const activeItems =
    tableSearchTerm?.trim().length > 0 ? tableSearchItems : items;
  const effectivePage =
    tableSearchTerm?.trim().length > 0 ? tableSearchPage : page;
  const effectivePageSize =
    tableSearchTerm?.trim().length > 0 ? tableSearchPageSize : pageSize;
  const effectiveTotal =
    tableSearchTerm?.trim().length > 0 ? tableSearchTotal : total;

  const displayItems = useMemo(() => {
    if (filterMode !== 'low') return activeItems;
    return activeItems.filter(isBackofficeProductLowStock);
  }, [activeItems, filterMode]);

  const lowStockItemsCount = useMemo(
    () => activeItems.filter(isBackofficeProductLowStock).length,
    [activeItems],
  );

  const emptyState = useMemo(() => {
    const term = String(tableSearchTerm ?? '').trim();
    const isSearchMode = term.length > 0;
    const inventoryEmpty = (items?.length ?? 0) === 0;
    const viewEmpty = (displayItems?.length ?? 0) === 0;
    const isLowFilter = filterMode === 'low';

    const noSearchResults = isSearchMode && viewEmpty && !inventoryEmpty;
    const filterEmpty =
      !isSearchMode && isLowFilter && viewEmpty && !inventoryEmpty;

    const title = noSearchResults
      ? `No results for "${term}"`
      : filterEmpty
        ? 'No low stock products'
        : inventoryEmpty
          ? 'No products yet'
          : 'No products found';

    const subtitle = noSearchResults
      ? 'Not found in inventory. Create it as a new product?'
      : filterEmpty
        ? 'Switch to "All" to view all products.'
        : inventoryEmpty
          ? 'Add products to get started'
          : 'Try again later';

    const ctaText = noSearchResults ? `Add "${term}"` : 'Add Product';

    return {
      term,
      isSearchMode,
      inventoryEmpty,
      viewEmpty,
      filterEmpty,
      noSearchResults,
      title,
      subtitle,
      ctaText,
    };
  }, [tableSearchTerm, items?.length, displayItems?.length, filterMode]);

  /*
  // Table data retained for future use
  const tableData = activeItems.map((p, idx) => [
    String((effectivePage - 1) * effectivePageSize + idx + 1),
    `${p.bp_product_name}`.trim(),
    p.bp_product_category || '—',
    p.bp_product_sub_category || '—',
    p.bp_product_buying_price != null
      ? formatWithCommas(Number(p.bp_product_buying_price || 0), 0)
      : '—',
    p.bp_product_selling_price != null
      ? formatWithCommas(Number(p.bp_product_selling_price || 0), 0)
      : '—',
    String(p.bp_product_quantity ?? 0),
    String(p.bp_minimum_stock_level ?? '—'),
  ]);

  const from = (effectivePage - 1) * effectivePageSize;
  const to = Math.min(effectivePage * effectivePageSize, effectiveTotal);
  const fromIndex = effectiveTotal === 0 ? 0 : from + 1;
  const toIndex = effectiveTotal === 0 ? 0 : to;
  */

  const onPressItem = useCallback(
    (product: any) => {
      if (!product) return;
      dispatch(setSelectedProductId(product._id));
      // @ts-ignore route exists after we add it
      nav.navigate('BackOfficeProductDetails', { productId: product._id });
    },
    [dispatch, nav],
  );

  const hasNextPage = useMemo(() => {
    const totalCount = Number(effectiveTotal || 0);
    const size = Number(effectivePageSize || 1);
    const current = Number(effectivePage || 1);
    const maxPages = Math.max(1, Math.ceil(totalCount / size));
    return current < maxPages;
  }, [effectiveTotal, effectivePageSize, effectivePage]);

  const loadMore = useCallback(async () => {
    if (loadMoreLockRef.current || isLoadingMore || loading || !hasNextPage)
      return;
    loadMoreLockRef.current = true;
    if (!owner?.accessToken || !ownerId) return;
    try {
      setIsLoadingMore(true);
      const totalCount = Number(effectiveTotal || 0);
      const size = Number(effectivePageSize || 1);
      const current = Number(effectivePage || 1);
      const maxPages = Math.max(1, Math.ceil(totalCount / size));
      const nextPage = current + 1;
      if (nextPage > maxPages) {
        return;
      }

      if (tableSearchTerm?.trim().length > 0) {
        const res = await searchOwnerProducts(
          axiosBackoffice as any,
          tableSearchTerm,
          nextPage,
          tableSearchPageSize,
          { accessToken: owner?.accessToken },
        );
        if (res?.status === 200) {
          const docs = res?.data?.data?.data || [];
          const totalCount =
            res?.data?.data?.total || tableSearchItems.length + docs.length;
          dispatch(
            setTableSearchResults({
              items: [...tableSearchItems, ...docs],
              total: totalCount,
              page: nextPage,
              pageSize: tableSearchPageSize,
            }),
          );
        } else {
          dispatch(
            setProductsError(
              String(res?.data?.message || 'Failed to load more products'),
            ),
          );
        }
      } else {
        const res = await listOwnerProducts(
          axiosBackoffice as any,
          ownerId,
          nextPage,
          pageSize,
          { accessToken: owner?.accessToken },
        );
        if (res?.status === 200) {
          const docs = res?.data?.data?.data || [];
          const totalCount =
            res?.data?.data?.total || items.length + docs.length;
          dispatch(
            setProducts({
              items: [...items, ...docs],
              total: totalCount,
              page: nextPage,
              pageSize,
            }),
          );
        } else {
          dispatch(
            setProductsError(
              String(res?.data?.message || 'Failed to load more products'),
            ),
          );
        }
      }
    } catch (e: any) {
      dispatch(setProductsError(e?.message || 'Failed to load more products'));
    } finally {
      setIsLoadingMore(false);
      loadMoreLockRef.current = false;
    }
  }, [
    axiosBackoffice,
    dispatch,
    effectiveTotal,
    effectivePage,
    effectivePageSize,
    hasNextPage,
    isLoadingMore,
    items,
    loading,
    owner?.accessToken,
    ownerId,
    page,
    pageSize,
    tableSearchItems,
    tableSearchPage,
    tableSearchPageSize,
    tableSearchTerm,
  ]);

  const onRefresh = useCallback(async () => {
    if (refreshing || loading || !owner?.accessToken || !ownerId) return;

    setRefreshing(true);
    loadMoreLockRef.current = false;

    try {
      dispatch(setProductsLoading(true));
      dispatch(setProductsError(null));

      const trimmedSearch = String(tableSearchTerm || '').trim();
      if (trimmedSearch.length > 0) {
        const res = await searchOwnerProducts(
          axiosBackoffice as any,
          trimmedSearch,
          1,
          tableSearchPageSize,
          { accessToken: owner.accessToken },
        );

        if (res?.status === 200) {
          const docs = res?.data?.data?.data || [];
          const totalCount = res?.data?.data?.total || docs.length;
          dispatch(
            setTableSearchResults({
              items: docs,
              total: totalCount,
              page: 1,
              pageSize: tableSearchPageSize,
            }),
          );
        } else {
          dispatch(
            setProductsError(
              String(res?.data?.message || 'Failed to refresh products'),
            ),
          );
        }

        return;
      }

      const res = await listOwnerProducts(
        axiosBackoffice as any,
        ownerId,
        1,
        pageSize,
        { accessToken: owner.accessToken },
      );

      if (res?.status === 200) {
        const docs = res?.data?.data?.data || [];
        const totalCount = res?.data?.data?.total || docs.length;
        dispatch(
          setProducts({
            items: docs,
            total: totalCount,
            page: 1,
            pageSize,
          }),
        );
      } else {
        dispatch(
          setProductsError(
            String(res?.data?.message || 'Failed to refresh products'),
          ),
        );
      }
    } catch (e: any) {
      dispatch(setProductsError(e?.message || 'Failed to refresh products'));
    } finally {
      dispatch(setProductsLoading(false));
      setRefreshing(false);
    }
  }, [
    axiosBackoffice,
    dispatch,
    loading,
    owner?.accessToken,
    ownerId,
    pageSize,
    refreshing,
    tableSearchPageSize,
    tableSearchTerm,
  ]);

  const renderItem = useCallback(
    ({ item, index }: { item: any; index: number }) => {
      const imageUri = String(item?.bp_product_image || '');
      const rowNumber = index + 1;
      const hasVariants = isVariantBackofficeProduct(item);
      const priceType = String(item?.bp_product_price_type || '').toLowerCase();
      const isUnit = priceType === 'unit';
      const unit = String(item?.bp_product_unit_of_mass || '').trim();
      const qty = hasVariants
        ? getVariantAggregateStock(item)
        : Number(item?.bp_product_quantity ?? 0);
      const stockLabel =
        isUnit && unit
          ? `${formatWithCommas(qty, 0)} ${unit}`
          : formatWithCommas(qty, 0);
      const isLowOrZeroStock = isBackofficeProductLowStock(item);
      const variantPriceRange = hasVariants
        ? getVariantPriceRangeLabel(item, currencyText)
        : item?.bp_product_selling_price != null
          ? `${currencyText} ${formatWithCommas(
              Number(item.bp_product_selling_price || 0),
              0,
            )}`
          : '—';
      const lowVariantLabels = hasVariants ? getLowVariantLabels(item) : [];
      // Variant parents keep raw name (mass lives on variants). Non-variants get mass/unit.
      const productName = hasVariants
        ? String(item?.bp_product_name || '').trim()
        : getProductDisplayName(item);
      return (
        <Pressable
          onPress={() => onPressItem(item)}
          style={({ pressed, hovered }: any) => [
            styles.listRow,
            pressed && styles.listRowPressed,
            Platform.OS === 'web' && hovered && styles.listRowHovered,
          ]}>
          <View style={styles.rowNumberColumn}>
            <Text style={styles.rowNumberText}>{rowNumber}</Text>
          </View>
          <View style={styles.leftBlock}>
            {imageUri ? (
              <FastImage
                source={{
                  uri: forceHttps(imageUri),
                  priority: FastImage.priority.normal,
                  cache: FastImage.cacheControl.immutable,
                }}
                style={styles.productImage as any}
                resizeMode={FastImage.resizeMode.cover}
              />
            ) : (
              <View style={styles.productImagePlaceholder}>
                <Icon
                  source="image-plus"
                  size={iconSize(16)}
                  color={theme.colors.textSecondary}
                />
              </View>
            )}
            <View style={styles.titleWrap}>
              <Text numberOfLines={1} style={styles.productName}>
                {String(productName || '')}
                {hasVariants ? (
                  <Text style={styles.variantInlineText}>
                    {` • ${getProductVariants(item).length} ${
                      getProductVariants(item).length === 1 ? 'variant' : 'variants'
                    }`}
                  </Text>
                ) : null}
              </Text>
              {!!item?.bp_product_category && (
                <Text numberOfLines={1} style={styles.productCategory}>
                  {String(item?.bp_product_category)}
                </Text>
              )}
              {lowVariantLabels.length > 0 ? (
                <Text numberOfLines={2} style={styles.lowVariantText}>
                  {`Low variants: ${lowVariantLabels.join(', ')}`}
                </Text>
              ) : null}
            </View>
          </View>
          <View style={styles.rightBlock}>
            <Text
              style={[
                styles.stockValue,
                isLowOrZeroStock && styles.stockValueLow,
              ]}>
              {stockLabel}
            </Text>
            <Text style={styles.priceRangeValue}>{variantPriceRange}</Text>
          </View>
        </Pressable>
      );
    },
    [currencyText, onPressItem, styles],
  );

  // Preload visible and upcoming images for smoother scrolling
  const viewabilityConfig = useRef({ itemVisiblePercentThreshold: 50 }).current;
  const displayItemsRef = useRef(displayItems);
  useEffect(() => {
    displayItemsRef.current = displayItems;
  }, [displayItems]);

  // IMPORTANT: FlatList does NOT support changing this callback after mount.
  // Keep the function identity stable and read latest data from a ref.
  const onViewableItemsChanged = useRef(({ viewableItems }: any) => {
    try {
      if (!Array.isArray(viewableItems) || viewableItems.length === 0) return;
      const data = displayItemsRef.current ?? [];
      const firstIndex = viewableItems[0]?.index ?? 0;
      const start = Math.max(0, firstIndex);
      const end = Math.min(data.length, start + 20);
      const toPreload = data.slice(start, end);
      const sources = toPreload
        .map((p: any) => String(p?.bp_product_image || ''))
        .filter(u => !!u)
        .map(uri => ({
          uri: forceHttps(uri),
          priority: FastImage.priority.normal,
        }));
      if (sources.length) FastImage.preload(sources as any);
    } catch {}
  }).current;

  // Initial preload for first items when data changes
  useEffect(() => {
    const initial = displayItems.slice(0, 24);
    const sources = initial
      .map((p: any) => String(p?.bp_product_image || ''))
      .filter(u => !!u)
      .map(uri => ({
        uri: forceHttps(uri),
        priority: FastImage.priority.high,
      }));
    if (sources.length) FastImage.preload(sources as any);
  }, [displayItems]);

  return (
    <View style={styles.container}>
      {/* Add Product moved to standalone screen */}
      {/* <Text style={styles.title}>Products</Text> */}
      {/* <Text style={styles.subtitle}>Manage product records</Text> */}
      <View style={styles.filterRow}>
        <Pressable
          onPress={() => setFilterMode('all')}
          style={[
            styles.chip,
            filterMode === 'all' ? styles.chipActive : styles.chipInactive,
          ]}>
          {filterMode === 'all' ? (
            <MaterialCommunityIcons
              name="check-circle"
              size={fs(14)}
              color={theme.colors.primaryDark}
            />
          ) : null}
          <Text
            style={[
              styles.chipText,
              filterMode === 'all'
                ? styles.chipTextActive
                : styles.chipTextInactive,
            ]}>
            {`All (${activeItems.length})`}
          </Text>
        </Pressable>
        <Pressable
          onPress={() => setFilterMode('low')}
          style={[
            styles.chip,
            filterMode === 'low' ? styles.chipActiveLow : styles.chipInactive,
          ]}>
          {filterMode === 'low' ? (
            <MaterialCommunityIcons
              name="check-circle"
              size={fs(14)}
              color={theme.colors.error}
            />
          ) : null}
          <Text
            style={[
              styles.chipText,
              filterMode === 'low'
                ? styles.chipTextActiveLow
                : styles.chipTextInactive,
            ]}>
            {`Low stocks (${lowStockItemsCount})`}
          </Text>
        </Pressable>
      </View>

      <View style={styles.tableContainer}>
        {/**
         * Legacy table view preserved for future use
         *
         * <AppDataTable
         *   columns={columns}
         *   data={tableData}
         *   loading={loading}
         *   hideToolbarWhenRotated
         *   onRowPress={onRowPress}
         *   keyExtractor={(_, i) => String(activeItems[i]?._id || i)}
         *   emptyMessage="No products found"
         *   toolbar={
         *     <View style={styles.toolbarRow}>
         *       <Searchbar
         *         placeholder="Search products by name"
         *         value={tableSearchTerm}
         *         onChangeText={text => dispatch(setTableSearchTerm(text))}
         *         style={styles.searchbar}
         *         inputStyle={styles.searchbarInput}
         *         iconColor={theme.colors.textSecondary}
         *         placeholderTextColor={theme.colors.textSecondary}
         *       />
         *     </View>
         *   }
         *   footerPagination={{
         *     page: Math.max(0, effectivePage - 1),
         *     numberOfPages: Math.max(
         *       1,
         *       Math.ceil(effectiveTotal / effectivePageSize),
         *     ),
         *     onPageChange: p => {
         *       if (tableSearchTerm?.trim().length > 0) {
         *         dispatch(setTableSearchPagination({ page: p + 1 }));
         *       } else {
         *         dispatch(setProductsPagination({ page: p + 1 }));
         *       }
         *     },
         *     label: `${fromIndex}-${toIndex} of ${effectiveTotal}`,
         *     showFastPaginationControls: true,
         *     numberOfItemsPerPageList: [100, 200, 500],
         *     numberOfItemsPerPage: effectivePageSize,
         *     onItemsPerPageChange: size => {
         *       const next = Number(size || 100);
         *       if (tableSearchTerm?.trim().length > 0) {
         *         dispatch(setTableSearchPagination({ page: 1, pageSize: next }));
         *       } else {
         *         dispatch(setProductsPagination({ page: 1, pageSize: next }));
         *       }
         *     },
         *     selectPageDropdownLabel: 'Rows per page',
         *   }}
         * />
         */}
        <FlatList
          data={displayItems}
          keyExtractor={item => String(item?._id)}
          renderItem={renderItem}
          numColumns={Platform.OS === 'web' ? 2 : 1}
          key={Platform.OS === 'web' ? 'web-2col' : 'native-1col'}
          columnWrapperStyle={
            Platform.OS === 'web' ? styles.columnWrapper : undefined
          }
          {...(Platform.OS !== 'web'
            ? {
                maintainVisibleContentPosition: {
                  minIndexForVisible: 0,
                  autoscrollToTopThreshold: 10,
                },
              }
            : {})}
          onEndReached={loadMore}
          onEndReachedThreshold={0.5}
          refreshing={refreshing}
          onRefresh={onRefresh}
          onViewableItemsChanged={onViewableItemsChanged}
          viewabilityConfig={viewabilityConfig}
          stickyHeaderIndices={[0]}
          ListHeaderComponent={() => (
            <View style={styles.listHeaderRow}>
              <Text style={styles.headerNumberTitle}>#</Text>
              <Text style={[styles.headerTitleLeft, styles.headerTitleProduct]}>
                Product Name
              </Text>
              <Text style={styles.headerTitleRight}>Stock/Price</Text>
            </View>
          )}
          ListEmptyComponent={() =>
            loading ? (
              <View style={styles.loadingWrap}>
                <ActivityIndicator />
                <Text style={styles.loadingText}>Loading products...</Text>
              </View>
            ) : (
              <View style={styles.emptyAdminContainer}>
                <MaterialCommunityIcons
                  name="package-variant"
                  size={iconSize(64)}
                  color={theme.colors.textTertiary}
                />
                <Text style={styles.emptyText}>{emptyState.title}</Text>
                <Text style={styles.emptySubtext}>{emptyState.subtitle}</Text>
                <Button
                  backgroundColor={theme.colors.success}
                  rippleColor={theme.colors.success}
                  text={emptyState.ctaText}
                  fontFamily={GTFonts.regular}
                  icon=""
                  height={44}
                  paddingHorizontal={44}
                  borderRadius={5}
                  onPress={() => nav.navigate('BackOfficeAddProduct')}
                />
              </View>
            )
          }
          ListFooterComponent={() =>
            isLoadingMore ? (
              <View style={styles.footerLoading}>
                <ActivityIndicator />
              </View>
            ) : null
          }
        />
      </View>
    </View>
  );
};

export default BackOfficeProductsScreen;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      padding: s(5),
      backgroundColor: theme.colors.background,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        padding: s(24),
      }),
    },
    title: {
      fontSize: fs(24),
      lineHeight: fs(28),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      marginBottom: v(6),
    },
    subtitle: {
      fontSize: fs(14),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      marginBottom: v(10),
    },
    loadingWrap: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: s(40),
    },
    loadingText: {
      marginTop: v(12),
      fontSize: fs(16),
      lineHeight: fs(20),
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
    },
    emptyWrap: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: s(40),
    },
    emptyAdminContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: s(60),
      paddingHorizontal: s(20),
      gap: v(16),
    },
    emptyText: {
      fontSize: fs(16),
      lineHeight: fs(20),
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
    },
    emptySubtext: {
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.textTertiary,
      fontFamily: GTFonts.regular,
      textAlign: 'center',
    },
    tableContainer: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      overflow: 'hidden',
      ...theme.shadows.sm,
    },
    addProductBtn: {
      marginLeft: Platform.OS === 'web' ? s(8) : 0,
      paddingHorizontal: s(10),
      paddingVertical: s(6),
      borderRadius: theme.borderRadius.md,
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease-out',
      }),
    } as any,
    addProductBtnHovered: {
      backgroundColor: 'rgba(255, 255, 255, 0.14)',
    } as any,
    addProductBtnPressed: {
      opacity: 0.75,
    },
    addProductBtnText: {
      color: theme.colors.textInverse,
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(16),
    },
    toolbarRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
      marginBottom: v(8),
    },
    searchbar: {
      width: '100%',
      maxWidth: WEB_MAX_CONTENT_WIDTH,
      height: v(50),
      minHeight: v(50),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surface,
      borderWidth: 1,
      borderColor: theme.colors.border,
      justifyContent: 'center',
    },
    searchbarInput: {
      fontSize: fs(14),
      fontFamily: GTFonts.regular,
      color: theme.colors.text,
      alignSelf: 'center',
    },
    listHeaderRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: s(12),
      paddingVertical: Platform.OS === 'web' ? s(14) : s(10),
      backgroundColor: theme.colors.surfaceSecondary,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
    },
    headerTitleLeft: {
      fontSize: Platform.OS === 'web' ? fs(18) : fs(16),
      lineHeight: Platform.OS === 'web' ? fs(22) : fs(18),
      fontFamily: GTFonts.bold,
      color: theme.colors.text,
    },
    headerTitleProduct: {
      flex: 1,
    },
    headerNumberTitle: {
      width: s(16),
      marginRight: s(10),
      fontSize: Platform.OS === 'web' ? fs(18) : fs(16),
      lineHeight: Platform.OS === 'web' ? fs(22) : fs(18),
      fontFamily: GTFonts.bold,
      color: theme.colors.text,
      textAlign: 'center',
    },
    headerTitleRight: {
      fontSize: Platform.OS === 'web' ? fs(18) : fs(16),
      lineHeight: Platform.OS === 'web' ? fs(22) : fs(18),
      fontFamily: GTFonts.bold,
      color: theme.colors.text,
      textAlign: 'right',
      minWidth: s(80),
    },
    columnWrapper: {
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
    },
    listRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: Platform.OS === 'web' ? s(16) : s(12),
      paddingVertical: Platform.OS === 'web' ? s(14) : s(10),
      borderBottomWidth: Platform.OS === 'web' ? 0 : 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web' && {
        flex: 0.5,
        borderRightWidth: 1,
        borderRightColor: theme.colors.border,
        cursor: 'pointer',
        transition: 'background-color 0.15s ease-out, transform 0.15s ease-out',
      }),
    } as any,
    listRowHovered: {
      backgroundColor: theme.colors.surfaceSecondary,
      transform: [{ translateY: -1 }],
    } as any,
    listRowPressed: {
      opacity: 0.75,
    },
    leftBlock: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      gap: s(10),
    },
    rowNumberColumn: {
      width: s(16),
      marginRight: s(10),
      alignItems: 'center',
      justifyContent: 'center',
    },
    rowNumberText: {
      fontSize: Platform.OS === 'web' ? fs(15) : fs(13),
      lineHeight: Platform.OS === 'web' ? fs(18) : fs(16),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      textAlign: 'center',
    },
    productImage: {
      width: Platform.OS === 'web' ? s(52) : s(40),
      height: Platform.OS === 'web' ? s(52) : s(40),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surfaceSecondary,
    },
    productImagePlaceholder: {
      width: Platform.OS === 'web' ? s(52) : s(40),
      height: Platform.OS === 'web' ? s(52) : s(40),
      borderRadius: theme.borderRadius.md,
      borderStyle: 'dashed',
      borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      borderColor: theme.colors.primaryLight,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.surfaceSecondary,
    },
    titleWrap: {
      flex: 1,
      minWidth: 0,
    },
    productName: {
      fontSize: Platform.OS === 'web' ? fs(16) : fs(15),
      lineHeight: Platform.OS === 'web' ? fs(20) : fs(18),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
    },
    variantInlineText: {
      fontSize: Platform.OS === 'web' ? fs(13) : fs(13),
      lineHeight: Platform.OS === 'web' ? fs(20) : fs(18),
      fontFamily: GTFonts.regular,
      color: theme.colors.infoDark,
    },
    productCategory: {
      fontSize: Platform.OS === 'web' ? fs(13) : fs(13),
      lineHeight: Platform.OS === 'web' ? fs(18) : fs(16),
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
    },
    lowVariantText: {
      marginTop: v(2),
      fontSize: fs(12),
      lineHeight: fs(16),
      fontFamily: GTFonts.regular,
      color: theme.colors.errorDark,
    },
    rightBlock: {
      minWidth: s(80),
      alignItems: 'flex-end',
      justifyContent: 'center',
      gap: v(3),
    },
    stockValue: {
      fontSize: Platform.OS === 'web' ? fs(16) : fs(14),
      lineHeight: Platform.OS === 'web' ? fs(20) : fs(18),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
    },
    priceRangeValue: {
      fontSize: fs(12),
      lineHeight: fs(16),
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
    },
    stockValueLow: {
      color: theme.colors.error,
    },
    footerLoading: {
      paddingVertical: s(12),
      alignItems: 'center',
      justifyContent: 'center',
    },
    filterRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
      marginBottom: v(12),
      flexWrap: 'wrap',
    },
    chip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(5),
      paddingHorizontal: s(14),
      paddingVertical: v(5),
      borderRadius: s(20),
      borderWidth: 1.2,
    },
    chipActive: {
      backgroundColor: theme.colors.primaryLight,
      borderColor: theme.colors.primaryDark,
    },
    chipActiveLow: {
      backgroundColor: theme.colors.errorLight,
      borderColor: theme.colors.error,
    },
    chipInactive: {
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.border,
    },
    chipText: {
      fontSize: fs(12.5),
      lineHeight: fs(18),
      fontFamily: GTFonts.semibold,
    },
    chipTextActive: {
      color: theme.colors.primaryDark,
    },
    chipTextActiveLow: {
      color: theme.colors.error,
    },
    chipTextInactive: {
      color: theme.colors.textSecondary,
    },
    hScroll: {
      flex: 1,
    },
    tableBorder: {
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    head: {
      height: v(56),
      backgroundColor: theme.colors.tableHeader,
    },
    headText: {
      margin: s(8),
      fontSize: fs(14),
      fontFamily: GTFonts.semibold,
      color: theme.colors.tableHeaderText,
      textAlign: 'center',
    },
    dataWrapper: {
      flex: 1,
    },
    rowTouchable: {
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
    },
    row: {
      height: v(60),
      backgroundColor: theme.colors.surface,
    },
    rowEven: {
      backgroundColor: theme.colors.surface,
    },
    rowOdd: {
      backgroundColor: theme.colors.surfaceSecondary,
    },
    rowText: {
      margin: s(8),
      fontSize: fs(14),
      fontFamily: GTFonts.regular,
      color: theme.colors.text,
      textAlign: 'center',
    },
    paginationContainer: {
      backgroundColor: theme.colors.surface,
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
      paddingVertical: s(16),
      paddingHorizontal: s(16),
    },
    paginationInfo: {
      alignItems: 'center',
      marginBottom: v(12),
    },
    paginationText: {
      fontSize: fs(14),
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
    },
    paginationBtns: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    paginationBtn: {
      backgroundColor: theme.colors.primary,
      paddingHorizontal: s(20),
      paddingVertical: s(12),
      borderRadius: theme.borderRadius.md,
      minWidth: s(80),
      alignItems: 'center',
    },
    paginationBtnDisabled: {
      backgroundColor: theme.colors.border,
    },
    paginationBtnText: {
      fontSize: fs(14),
      fontFamily: GTFonts.semibold,
      color: theme.colors.textInverse,
    },
    paginationBtnTextDisabled: {
      color: theme.colors.textSecondary,
    },
    pageIndicator: {
      backgroundColor: theme.colors.surfaceSecondary,
      paddingHorizontal: s(16),
      paddingVertical: s(8),
      borderRadius: theme.borderRadius.md,
    },
    pageText: {
      fontSize: fs(14),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
    },
  });
